import { confirmed, max, min, numeric, required } from "@vee-validate/rules";
import { configure, defineRule } from "vee-validate";
import { capitalize } from "vue";

// Define predefined rules
defineRule("required", required);
defineRule("min", min);
defineRule("max", max);
defineRule("maxLength", max); // Alias for backward compatibility
defineRule("isNumber", numeric); // Alias for backward compatibility
defineRule("numeric", numeric);
defineRule("confirmed", confirmed);

// Define custom rules
defineRule("isIP", (value: unknown) => {
  const ipRegex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return typeof value === "string" && ipRegex.test(value);
});

defineRule("isCIDR", (value: unknown) => {
  const cidrRegex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;
  return typeof value === "string" && cidrRegex.test(value);
});

defineRule("isPort", (value: unknown, _, ctx) => {
  const num = Number(value);
  if (isNaN(num) || num < 0 || num > 65535) {
    return `${capitalize(ctx.label ?? "")} phải là số từ 0 đến 65535`;
  }
  return true;
});

defineRule("validHost", (value: unknown, _, ctx) => {
  if (typeof value !== "string")
    return `${capitalize(ctx.label ?? "")} phải là chuỗi ký tự`;

  if (!value.startsWith("http://") && !value.startsWith("https://"))
    return `${capitalize(ctx.label ?? "")} thiếu giao thức (http:// hoặc https://)`;

  const hostWithoutProtocol = value.replace(/^https?:\/\//, "");
  const ipRegex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const domainRegex =
    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

  if (
    !ipRegex.test(hostWithoutProtocol) &&
    !domainRegex.test(hostWithoutProtocol)
  ) {
    return `${capitalize(
      ctx.label ?? "",
    )} phải là địa chỉ IP hoặc tên miền hợp lệ`;
  }

  return true;
});

defineRule("password_complex", (value: unknown, _, ctx) => {
  if (typeof value !== "string" || value.length === 0) return true; // Let 'required' handle empty

  const missing: string[] = [];

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(value)) {
    missing.push("một chữ thường");
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(value)) {
    missing.push("một chữ hoa");
  }

  // Check for at least one number
  if (!/\d/.test(value)) {
    missing.push("một số");
  }

  // Check for at least one special character
  if (!/[!@#$%^&*(),.?":{}|<>_\-+=/[\]\\';]/.test(value)) {
    missing.push("một ký tự đặc biệt");
  }

  if (missing.length > 0) {
    return `${capitalize(ctx.label ?? "")} phải chứa ít nhất: ${missing.join(
      ", ",
    )}`;
  }

  return true;
});

// Configure global messages
configure({
  generateMessage: (ctx) => {
    const { rule, field } = ctx;
    const label = ctx.label || field;
    const name = capitalize(label);
    const params = rule?.params as unknown[];

    switch (rule?.name) {
      case "required":
        return `${name} là bắt buộc`;

      case "min": {
        const limit = params[0];
        if (typeof ctx.value === "number") {
          return `${name} không được nhỏ hơn ${limit}`;
        }
        if (Array.isArray(ctx.value)) {
          return `${name} phải có ít nhất ${limit} mục`;
        }
        return `${name} phải có ít nhất ${limit} ký tự`;
      }

      case "max":
      case "maxLength": {
        const limit = params[0];
        if (typeof ctx.value === "number") {
          return `${name} không được lớn hơn ${limit}`;
        }
        if (Array.isArray(ctx.value)) {
          return `${name} phải có tối đa ${limit} mục`;
        }
        return `${name} phải có tối đa ${limit} ký tự`;
      }

      case "isNumber":
      case "numeric":
        return `${name} phải là số`;

      case "isIP":
        return `${name} phải là địa chỉ IP hợp lệ`;

      case "isCIDR":
        return `${name} phải là dải IP hợp lệ (CIDR)`;

      case "confirmed":
        return `${name} không khớp`;

      default:
        return `${name} không hợp lệ`;
    }
  },
});
